import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Heart, Copy, UserPlus, Home, Award, Lock, Sparkles, Users, Check, X } from 'lucide-react';
import api from '../lib/api';
import { CHARACTERS } from '../lib/levels';

const MODE_COPY = {
  one_home:  { label: 'One home',  tagline: 'One home. One team.',                  desc: 'The whole family lives together.' },
  two_homes: { label: 'Two homes', tagline: 'Two homes. One child. One goal.',      desc: "Co-parenting mode: your child's progress is always shared with both parents automatically." },
};

const CREW_ROLE_LABEL = { grandparent: 'Grandparent', uncle: 'Uncle', aunt: 'Aunt', family_friend: 'Family friend' };

export default function FamilyTab() {
  const [family, setFamily] = useState(null);
  const [badgesData, setBadgesData] = useState({ badges: [], catalog: [], total_earned: 0 });
  const [crewData, setCrewData] = useState({ pending: [], approved: [] });
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [modeBusy, setModeBusy] = useState(false);
  const [approvedCode, setApprovedCode] = useState(null);

  const load = async () => {
    try {
      const [f, b, c] = await Promise.all([
        api.get('/parent/family'),
        api.get('/parent/badges'),
        api.get('/parent/crew'),
      ]);
      setFamily(f.data);
      setBadgesData(b.data);
      setCrewData(c.data);
    } catch (e) { toast.error('Failed to load family info'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    try {
      const { data } = await api.post('/parent/invite-co-parent', { email: inviteEmail.trim() });
      toast.success('Invite code created!');
      setInviteEmail('');
      await load();
      try { await navigator.clipboard.writeText(data.code); toast.success(`Code ${data.code} copied to clipboard`); } catch {}
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setInviteBusy(false); }
  };

  const acceptInvite = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoinBusy(true);
    try {
      await api.post(`/parent/accept-invite/${joinCode.trim().toUpperCase()}`);
      toast.success("You're linked! 🎉");
      setJoinCode('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Invalid code'); }
    finally { setJoinBusy(false); }
  };

  const changeMode = async (mode) => {
    if (mode === family?.family_mode) return;
    setModeBusy(true);
    try {
      const { data } = await api.patch('/parent/family-mode', { mode });
      toast.success(`Mode set: ${data.tagline}`);
      load();
    } catch (e) { toast.error('Failed to update'); }
    finally { setModeBusy(false); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`Copied ${code}`); }
    catch { toast.error('Copy failed'); }
  };

  const approveCrew = async (id, name) => {
    try {
      const { data } = await api.post(`/parent/approve-crew/${id}`);
      toast.success(`${name} approved!`);
      setApprovedCode({ code: data.invite_code, name, email: data.invitee_email });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const revokeCrew = async (id) => {
    if (!window.confirm('Revoke this crew member?')) return;
    try {
      await api.post(`/parent/revoke-crew/${id}`);
      toast.success('Access revoked');
      load();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="p-6 text-center text-navy/60 font-heading">Loading family…</div>;

  const mode = family?.family_mode || 'one_home';
  const modeCopy = MODE_COPY[mode];

  return (
    <div className="space-y-5" data-testid="family-tab">
      {/* Header + tagline */}
      <section className="rounded-3xl bg-gradient-to-br from-navy via-teal to-navy text-white p-5 shadow-squishyLg" data-testid="family-header">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Heart className="w-7 h-7 text-gold" fill="currentColor" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-gold text-[11px] font-heading font-bold uppercase tracking-wider">Family</div>
            <h2 className="font-heading text-2xl font-bold leading-tight">{modeCopy.tagline}</h2>
            <p className="text-white/85 text-sm mt-1 leading-snug">Your child is the hero. You are the support crew.</p>
          </div>
        </div>
      </section>

      {/* Family mode toggle */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="family-mode-card">
        <div className="flex items-center gap-2 mb-3">
          <Home className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">Family setup</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {['one_home', 'two_homes'].map((m) => {
            const c = MODE_COPY[m];
            const active = mode === m;
            return (
              <button key={m} onClick={() => changeMode(m)} disabled={modeBusy}
                className={`text-left p-4 rounded-2xl border-2 transition-colors ${active ? 'border-teal bg-teal/10' : 'border-slate-200 hover:border-teal/50'}`}
                data-testid={`mode-${m}`}>
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold text-navy">{c.label}</span>
                  {active && <span className="text-[10px] font-heading font-bold text-teal uppercase">Active</span>}
                </div>
                <div className="text-xs text-navy/70 mt-1 leading-snug">{c.desc}</div>
              </button>
            );
          })}
        </div>
        {mode === 'two_homes' && (
          <div className="mt-3 rounded-2xl bg-navy text-white p-3 text-xs leading-relaxed flex items-start gap-2" data-testid="two-homes-note">
            <Lock className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" />
            <span>In co-parenting mode, your child's progress and goals are shared with both parents automatically. Neither parent can hide progress from the other. The app never takes sides.</span>
          </div>
        )}
      </section>

      {/* Co-parent status */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="co-parent-card">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">Co-parent</h3>
        </div>

        {family?.co_parent ? (
          <div className="rounded-2xl bg-gradient-to-br from-teal/15 to-gold/15 border-2 border-teal p-4 flex items-center gap-3" data-testid="co-parent-linked">
            <div className="w-12 h-12 rounded-full bg-navy text-gold flex items-center justify-center font-heading text-lg font-bold">
              {(family.co_parent.name || '?')[0]}
            </div>
            <div className="min-w-0">
              <div className="font-heading font-bold text-navy">{family.co_parent.name}</div>
              <div className="text-xs text-navy/70">{family.co_parent.email}</div>
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-heading font-bold text-teal uppercase">
                <Heart className="w-3 h-3 fill-current" /> Connected
              </div>
            </div>
          </div>
        ) : family?.pending_invite ? (
          <div className="rounded-2xl border-2 border-gold bg-gold/10 p-4" data-testid="pending-invite">
            <div className="text-xs text-navy/70">Invite pending for</div>
            <div className="font-heading font-bold text-navy">{family.pending_invite.invitee_email}</div>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 font-mono font-bold text-navy text-lg tracking-widest bg-white px-3 py-2 rounded-xl border-2 border-slate-200" data-testid="invite-code">{family.pending_invite.code}</div>
              <button onClick={() => copyCode(family.pending_invite.code)}
                className="px-3 py-2 rounded-xl bg-navy text-white font-heading font-bold text-xs inline-flex items-center gap-1"
                data-testid="copy-invite-code"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
            <p className="text-xs text-navy/70 mt-2">Share this code with your co-parent — they enter it in their Family tab to join.</p>
          </div>
        ) : (
          <>
            <form onSubmit={sendInvite} className="space-y-2" data-testid="invite-form">
              <label className="block text-xs font-heading font-bold text-navy/70 uppercase">Invite the other parent by email</label>
              <div className="flex gap-2">
                <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="coparent@email.com"
                  className="flex-1 h-11 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
                  data-testid="invite-email-input" />
                <button type="submit" disabled={inviteBusy}
                  className="px-4 rounded-2xl bg-gold text-navy font-heading font-bold text-sm shadow-gold disabled:opacity-60"
                  data-testid="invite-submit">
                  {inviteBusy ? '…' : 'Invite'}
                </button>
              </div>
            </form>
            <div className="my-3 flex items-center gap-2 text-xs text-navy/50 font-heading">
              <div className="flex-1 h-px bg-slate-200" />or<div className="flex-1 h-px bg-slate-200" />
            </div>
            <form onSubmit={acceptInvite} className="space-y-2" data-testid="join-form">
              <label className="block text-xs font-heading font-bold text-navy/70 uppercase">Have a code from your co-parent?</label>
              <div className="flex gap-2">
                <input type="text" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="6-char code"
                  className="flex-1 h-11 px-3 rounded-2xl border-2 border-slate-200 focus:border-teal focus:outline-none font-mono uppercase text-sm tracking-widest"
                  maxLength={6}
                  data-testid="join-code-input" />
                <button type="submit" disabled={joinBusy}
                  className="px-4 rounded-2xl bg-navy text-white font-heading font-bold text-sm disabled:opacity-60"
                  data-testid="join-submit">
                  {joinBusy ? '…' : 'Join'}
                </button>
              </div>
            </form>
          </>
        )}
      </section>

      {/* Parent Badge Wall */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="parent-badges-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-gold" />
            <h3 className="font-heading text-lg font-bold text-navy">Your parent badges</h3>
          </div>
          <div className="text-xs font-heading font-bold text-navy/60">
            {badgesData.total_earned} / {badgesData.catalog.length} earned
          </div>
        </div>

        {badgesData.badges.length > 0 && (
          <div className="rounded-2xl bg-gradient-to-r from-gold/15 to-teal/15 border-2 border-gold p-3 mb-3 flex items-start gap-2" data-testid="parent-reward-callout">
            <Sparkles className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <div className="text-xs text-navy leading-relaxed">
              <span className="font-heading font-bold">This week your child earned {badgesData.total_earned} achievements. So did you.</span>
              <div className="text-navy/70 mt-0.5">You both played a role in this.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {badgesData.catalog.map((b) => (
            <div key={b.key}
              className={`rounded-2xl p-3 border-2 text-center transition-colors ${b.earned ? 'border-gold bg-gold/10' : 'border-slate-200 bg-slate-50 opacity-60'}`}
              data-testid={`badge-${b.key}`}>
              <div className={`text-4xl ${b.earned ? '' : 'grayscale'}`}>{b.emoji}</div>
              <div className={`font-heading font-bold text-[11px] mt-1 leading-tight ${b.earned ? 'text-navy' : 'text-navy/60'}`}>{b.label}</div>
              <div className="text-[10px] text-navy/60 mt-1 leading-tight">{b.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Children in this family */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="family-children-card">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-teal" fill="currentColor" />
          <h3 className="font-heading text-lg font-bold text-navy">Children in your family</h3>
        </div>
        {family?.children?.length === 0 ? (
          <p className="text-navy/60 text-sm">No children linked yet — ask them to register with your email.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {family.children.map((c) => {
              const ch = CHARACTERS[c.character] || CHARACTERS.stupidopoulou;
              return (
                <div key={c.id} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-cream border-2 border-cream" data-testid={`family-child-${c.id}`}>
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white">
                    <img src={ch.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <span className="font-heading font-bold text-navy text-sm">{c.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Support crew management */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="crew-manage-card">
        <div className="flex items-center gap-2 mb-3">
          <Users className="w-5 h-5 text-teal" />
          <h3 className="font-heading text-lg font-bold text-navy">Support crew</h3>
        </div>

        {crewData.pending.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] font-heading font-bold text-gold uppercase tracking-wider mb-2">Waiting for your approval</div>
            <div className="space-y-2">
              {crewData.pending.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-3 rounded-2xl border-2 border-gold bg-gold/10" data-testid={`pending-crew-${p.id}`}>
                  <div className="text-2xl">{p.role_emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-navy text-sm">{p.invitee_name}</div>
                    <div className="text-xs text-navy/70 truncate">{p.invitee_email} · <span className="font-semibold">{p.role_label}</span> for {p.child_name}</div>
                  </div>
                  <button onClick={() => approveCrew(p.id, p.invitee_name)}
                    className="px-3 py-1.5 rounded-full bg-teal text-white font-heading font-bold text-xs inline-flex items-center gap-1"
                    data-testid={`approve-crew-${p.id}`}><Check className="w-3 h-3" /> Approve</button>
                  <button onClick={() => revokeCrew(p.id)} className="px-2 py-1.5 rounded-full bg-slate-100 text-navy/70 text-xs" data-testid={`deny-crew-${p.id}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {approvedCode && (
          <div className="mb-3 rounded-2xl border-2 border-teal bg-teal/10 p-3" data-testid="new-crew-code">
            <div className="text-xs text-navy/70">Share this code with <span className="font-heading font-bold">{approvedCode.name}</span> ({approvedCode.email}):</div>
            <div className="mt-2 flex items-center gap-2">
              <div className="flex-1 font-mono font-bold text-navy text-lg tracking-widest bg-white px-3 py-2 rounded-xl border-2 border-slate-200">{approvedCode.code}</div>
              <button onClick={() => copyCode(approvedCode.code)} className="px-3 py-2 rounded-xl bg-navy text-white font-heading font-bold text-xs inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
            </div>
            <p className="text-[11px] text-navy/60 mt-2">They register at the sign-up page with this code as an Extended Family member.</p>
          </div>
        )}

        {crewData.approved.length === 0 && crewData.pending.length === 0 ? (
          <p className="text-navy/60 text-sm">No crew members yet. When your child taps <span className="font-heading font-bold text-navy">"Invite Someone Special"</span>, requests appear here for your approval.</p>
        ) : crewData.approved.length > 0 && (
          <div>
            <div className="text-[10px] font-heading font-bold text-navy/60 uppercase tracking-wider mb-2">Active crew</div>
            <div className="space-y-2">
              {crewData.approved.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3 rounded-2xl bg-cream" data-testid={`approved-crew-${m.id}`}>
                  <div className="text-2xl">{m.role_emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-navy text-sm">{m.invitee_name}</div>
                    <div className="text-xs text-navy/70 truncate">{m.role_label} for {m.child_name} · {m.joined ? 'joined' : 'waiting to join'}</div>
                  </div>
                  <button onClick={() => revokeCrew(m.id)} className="px-3 py-1.5 rounded-full bg-slate-100 text-navy/70 font-heading font-bold text-xs" data-testid={`revoke-crew-${m.id}`}>Revoke</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
